import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import EmptyState from "../components/EmptyState/EmptyState";
import FooterHourlyTracking from "../components/footers/FooterHourlyTracking";
import NavBarHourlyTracking from "../components/navbars/NavBarHourlyTracking";
import PageContainer from "../components/pageContainer/PageContainer";
import TableHourTrackingMission from "../components/tables/TableHourTrackingMission";
import useTitle from "../hooks/TitleManager";
import UseContext from "../hooks/useContext";
import ApiDatabase from "../server";
import { forceDateToUtc0, dateToYYYYMMDD, calculateDateRange } from "../utils/Date";

const HourlyTracking = () => {
  useTitle({ title: "Suivi horaire" });
  const {
    slideOverDataReturn,
    setSlideOverDataReturn,
    missions,
    setMissions,
    companySelected,
    setCompanySelected,
    companyList,
    setCompanyList,
    dateSelect,
    setDateSelect,
  } = UseContext();
  const navigate = useNavigate();
  const [missionsToDisplay, setMissionsToDisplay] = useState([]);
  const [interimAgencyList, setInterimAgencyList] = useState([]);
  const [interimAgencySelected, setInterimAgencySelected] = useState([]);
  const [interimSectorList, setInterimSectorList] = useState([]);
  const [interimSectorSelected, setInterimSectorSelected] = useState([]);
  const [isWaitingMissions, setIsWaitingMissions] = useState(true);
  const [refreshMissions, setRefreshMissions] = useState(false);
  const [search, setSearch] = useState("");
  const [missionStatusSelected, setMissionStatusSelected] = useState([]);
  const [missionStatusList, setMissionStatusList] = useState([
    { type: "inProgress", name: "En cours", checked: false },
    { type: "flexibility", name: "Souplesse", checked: false },
    { type: "unsigned", name: "Non émargé", checked: false },
    { type: "shiftCompleted", name: "Shift terminé", checked: false },
    { type: "shiftMissing", name: "Shift manquant", checked: false },
    { type: "missingPhoneNumber", name: "Numéro manquant", checked: false },
  ]);

  const [currentPage, setCurrentPage] = useState(1);
  const countPerPage = 25;
  const [countTotal, setCountTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [initialParamsLoaded, setInitialParamsLoaded] = useState(false);
  const [isInterimAgencyListLoaded, setIsInterimAgencyListLoaded] =
    useState(false);
  const [isInterimSectorListLoaded, setIsInterimSectorListLoaded] =
    useState(false);
  const [isCompanyListLoaded, setIsCompanyListLoaded] = useState(false);

  let timerGetMissionSuggestions = useRef(null);
  
  // Tracks loaded date range to avoid redundant API calls during navigation
  const loadedDateRangeRef = useRef({ min: null, max: null });
  
  // Tracks if a request is currently in progress
  const isLoadingRef = useRef(false);

  const isDateInLoadedRange = (dateToCheck) => {
    const { min, max } = loadedDateRangeRef.current;
    if (!min || !max || !dateToCheck) return false;
    const dateTime = new Date(dateToCheck).setHours(0, 0, 0, 0);
    const minTime = new Date(min).setHours(0, 0, 0, 0);
    const maxTime = new Date(max).setHours(0, 0, 0, 0);
    return dateTime >= minTime && dateTime <= maxTime;
  };

  const isFullRangeLoaded = (centerDate) => {
    if (!centerDate) return false;
    const center = new Date(centerDate);
    center.setHours(0, 0, 0, 0);
    const dayMs = 24 * 60 * 60 * 1000;
    const minNeeded = new Date(center.getTime() - 2 * dayMs);
    const maxNeeded = new Date(center.getTime() + 2 * dayMs);
    return isDateInLoadedRange(minNeeded) && isDateInLoadedRange(maxNeeded);
  };

  // Updates the loaded date range to J-2 to J+2 around centerDate
  // Always resets to J-2 to J+2 around the current date (no extension beyond that)
  // Returns true if range was reset (non-contiguous jump)
  const updateLoadedDateRange = (centerDate) => {
    if (!centerDate) return false;
    
    // Normalize centerDate to midnight first, then calculate J-2 and J+2 using milliseconds
    const center = new Date(centerDate);
    center.setHours(0, 0, 0, 0);
    const dayMs = 24 * 60 * 60 * 1000;
    
    const newMin = new Date(center.getTime() - 2 * dayMs);
    const newMax = new Date(center.getTime() + 2 * dayMs);

    const { min: currentMin, max: currentMax } = loadedDateRangeRef.current;
    
    if (!currentMin || !currentMax) {
      loadedDateRangeRef.current = { min: newMin, max: newMax };
      return false;
    }

    const currentMinTime = new Date(currentMin).getTime();
    const currentMaxTime = new Date(currentMax).getTime();
    const newMinTime = newMin.getTime();
    const newMaxTime = newMax.getTime();
    
    const isContiguous = newMinTime <= currentMaxTime + dayMs && newMaxTime >= currentMinTime - dayMs;
    
    if (isContiguous) {
      // Always reset to J-2 to J+2 around current date (don't extend beyond)
      loadedDateRangeRef.current = { min: newMin, max: newMax };
      return false;
    } else {
      // Non-contiguous jump: reset to new range
      loadedDateRangeRef.current = { min: newMin, max: newMax };
      return true;
    }
  };

  const resetLoadedDateRange = () => {
    loadedDateRangeRef.current = { min: null, max: null };
  };

  useEffect(() => {
    if (missions.length > 0 && dateSelect) {
      const date = dateSelect.toISOString().split("T")[0];
      const dateNow = new Date().setHours(0, 0, 0, 0);

      let filteredByStatus;
      if (missionStatusSelected.length > 0) {
        filteredByStatus = missions.filter((mission) => {
          const dateMission = forceDateToUtc0(mission.date).setHours(
            0,
            0,
            0,
            0
          );
          const isDateBeforeDateNow = dateMission < dateNow;
          const isDateAfterDateNow = dateMission > dateNow;

          return missionStatusSelected.some((status) => {
            switch (status.type) {
              case "flexibility":
                return mission.isFlexibilityDate;
              case "inProgress":
                return (
                  (mission.markup.start &&
                    mission.markup.end &&
                    !isDateBeforeDateNow &&
                    !isDateAfterDateNow) ||
                  (mission.markup.start &&
                    !mission.markup.end &&
                    !isDateBeforeDateNow &&
                    !isDateAfterDateNow)
                );
              case "unsigned":
                return (
                  !mission.markup.start &&
                  !mission.markup.end &&
                  !isDateAfterDateNow
                );
              case "shiftCompleted":
                return (
                  mission.markup.start &&
                  mission.markup.end &&
                  !isDateAfterDateNow &&
                  isDateBeforeDateNow
                );
              case "shiftMissing":
                return (
                  mission.markup.start &&
                  !mission.markup.end &&
                  isDateBeforeDateNow &&
                  !isDateAfterDateNow
                );
              case "missingPhoneNumber":
                return !mission.user.phone;
              default:
                return false;
            }
          });
        });
      } else {
        filteredByStatus = missions;
      }

      const filteredByDate = filteredByStatus.filter((mission) => {
        const dateMission = new Date(mission.date).toISOString().split("T")[0];
        return dateMission === date;
      });

      const missionsByUserAndDate = {};

      filteredByDate.forEach((mission) => {
        const key = `${mission.user.idTempWorker}-${new Date(mission.date).toISOString().split("T")[0]}`;
        if (!missionsByUserAndDate[key]) {
          missionsByUserAndDate[key] = [];
        }
        missionsByUserAndDate[key].push(mission);
      });

      const finalMissions = [];

      // Sorting of assignments: non-flexible first, then flexible
      Object.keys(missionsByUserAndDate).forEach((key) => {
        const missions = missionsByUserAndDate[key];

        const nonFlexibleMissions = missions.filter(
          (mission) => mission.isFlexibilityDate === false
        );
        let flexibleMissions = missions.filter(
          (mission) => mission.isFlexibilityDate === true
        );

        // If we have both a non-flexible mission and a flexible mission, then we filter out the flexible missions whose "timesheet" is empty
        if (nonFlexibleMissions.length > 0 && flexibleMissions.length > 0) {
          flexibleMissions = flexibleMissions.filter(
            (mission) =>
              Array.isArray(mission.timeSheets) && mission.timeSheets.length > 0
          );
        }

        finalMissions.push(...nonFlexibleMissions);
        finalMissions.push(...flexibleMissions);
      });

      setCountTotal(finalMissions.length);
      setTotalPages(Math.ceil(finalMissions.length / countPerPage));
      const start = (currentPage - 1) * countPerPage;
      const end = start + countPerPage;

      setMissionsToDisplay(finalMissions.slice(start, end));
    } else {
      setMissionsToDisplay([]);
    }
  }, [
    missions,
    dateSelect,
    currentPage,
    countPerPage,
    missionStatusSelected,
    setMissionsToDisplay,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    dateSelect,
    search,
    companySelected,
    interimAgencySelected,
    interimSectorSelected,
    missionStatusList,
  ]);

  useEffect(() => {
    if (
      isInterimAgencyListLoaded &&
      isInterimSectorListLoaded &&
      isCompanyListLoaded &&
      missionStatusList.length > 0 &&
      !initialParamsLoaded
    ) {
      const params = new URLSearchParams(window.location.search);

      const date = params.get("dateSelect");
      if (date && new Date(date).toISOString() !== dateSelect?.toISOString()) {
        setDateSelect(new Date(date));
      }

      const searchParam = params.get("search");
      if (searchParam && searchParam !== search) {
        setSearch(searchParam);
      }

      const idCompany = params.get("companySelected");
      if (idCompany) {
        const selectedCompany = companyList.find(
          (company) => company.idCompany === idCompany
        );
        if (
          selectedCompany &&
          selectedCompany.idCompany !== companySelected?.idCompany
        ) {
          setCompanySelected(selectedCompany);
        }
      }

      const interimAgencies = params.get("interimAgencySelected");
      if (interimAgencies) {
        const selectedAgencies = interimAgencies
          .split(",")
          .map((name) =>
            interimAgencyList.find((agency) => agency.name === name)
          )
          .filter((agency) => agency);
        const updatedAgencyList = interimAgencyList.map((interimAgency) => ({
          ...interimAgency,
          checked: interimAgencies.split(",").includes(interimAgency.name),
        }));

        if (
          JSON.stringify(updatedAgencyList) !==
            JSON.stringify(interimAgencyList) &&
          JSON.stringify(selectedAgencies) !==
            JSON.stringify(interimAgencySelected)
        ) {
          setInterimAgencyList(updatedAgencyList);
          setInterimAgencySelected(selectedAgencies);
        }
      }

      const interimSectors = params.get("interimSectorSelected");
      if (interimSectors) {
        const selectedSectors = interimSectors
          .split(",")
          .map((name) =>
            interimSectorList.find((sector) => sector.name === name)
          )
          .filter((sector) => sector);
        const updatedInterimSectorList = interimSectorList.map(
          (interimSector) => ({
            ...interimSector,
            checked: interimSectors.split(",").includes(interimSector.name),
          })
        );

        if (
          JSON.stringify(updatedInterimSectorList) !==
            JSON.stringify(interimSectorList) &&
          JSON.stringify(selectedSectors) !==
            JSON.stringify(interimSectorSelected)
        ) {
          setInterimSectorList(updatedInterimSectorList);
          setInterimSectorSelected(selectedSectors);
        }
      }

      const missionStatuses = params.get("missionStatusList");
      if (missionStatuses) {
        const selectedMissionStatus = missionStatuses
          .split(",")
          .map((name) =>
            missionStatusList.find(
              (missionStatus) => missionStatus.name === name
            )
          )
          .filter((missionStatus) => missionStatus);
        const updatedMissionStatusList = missionStatusList.map(
          (missionStatus) => ({
            ...missionStatus,
            checked: missionStatuses.split(",").includes(missionStatus.name),
          })
        );

        if (
          JSON.stringify(updatedMissionStatusList) !==
            JSON.stringify(missionStatusList) &&
          JSON.stringify(selectedMissionStatus) !==
            JSON.stringify(missionStatusSelected)
        ) {
          setMissionStatusList(updatedMissionStatusList);
          setMissionStatusSelected(selectedMissionStatus);
        }
      }

      setInitialParamsLoaded(true);
    }
  }, [
    isInterimAgencyListLoaded,
    isInterimSectorListLoaded,
    isCompanyListLoaded,
    initialParamsLoaded,
    interimAgencyList,
    interimSectorList,
    missionStatusList,
    companyList,
    dateSelect,
    search,
    companySelected,
    interimAgencySelected,
    interimSectorSelected,
    missionStatusSelected,
    setCompanySelected,
    setDateSelect,
  ]);

  useEffect(() => {
    ApiDatabase.getInterimAgencyMissions(
      {},
      (data) => {
        if (data.interimAgencies?.length > 0) {
          const selectedAgencyNames = interimAgencySelected?.map(
            (agency) => agency.name
          );

          data.interimAgencies.forEach((agency) => {
            agency.checked = selectedAgencyNames.includes(agency.name);
          });

          setInterimAgencyList(data.interimAgencies);
        } else {
          setInterimAgencyList([]);
        }
        setIsInterimAgencyListLoaded(true);
      },
      (err) => {
        console.log(err);
        setIsInterimAgencyListLoaded(true);
      }
    );
  }, [
    refreshMissions,
    setInterimAgencySelected,
    setInterimAgencyList,
    interimAgencySelected,
  ]);

  useEffect(() => {
    ApiDatabase.getInterimSectorMissions(
      {},
      (data) => {
        if (data.interimSectors?.length > 0) {
          const selectedSectorNames = interimSectorSelected?.map(
            (sector) => sector.name
          );

          data.interimSectors.forEach((sector) => {
            sector.checked = selectedSectorNames.includes(sector.name);
          });

          setInterimSectorList(data.interimSectors);
        } else {
          setInterimSectorList([]);
        }
        setIsInterimSectorListLoaded(true);
      },
      (err) => {
        console.log(err);
        setIsInterimSectorListLoaded(true);
      }
    );
  }, [
    refreshMissions,
    setInterimSectorSelected,
    setInterimSectorList,
    interimSectorSelected,
  ]);

  useEffect(() => {
    ApiDatabase.getCompaniesMissions(
      {},
      (data) => {
        if (data.companies?.length > 0) {
          let selectedCompanyIndex = -1;

          if (companySelected) {
            selectedCompanyIndex = data.companies.findIndex(
              (company) => company._id === companySelected.idCompany
            );
          }

          data.companies.forEach((company, index) => {
            company.checked =
              index ===
              (selectedCompanyIndex !== -1 ? selectedCompanyIndex : 0);
          });

          if (selectedCompanyIndex === -1) {
            const firstCompany = data.companies[0];
            setCompanySelected({
              idCompany: firstCompany._id,
              name: firstCompany.companyName,
              syncDate: firstCompany.lastSyncDate,
              badgerName: firstCompany.badgerName,
            });
          }

          setCompanyList(
            data.companies.map((company) => ({
              idCompany: company._id,
              name: company.companyName || company.name,
              syncDate: company.lastSyncDate,
              badgerName: company.badgerName,
              checked: company.checked,
            }))
          );
        } else {
          setCompanyList([]);
        }
        setIsCompanyListLoaded(true);
      },
      (err) => {
        console.log(err);
        setIsCompanyListLoaded(true);
      }
    );
  }, [setCompanySelected, setCompanyList, companySelected]);

  useEffect(() => {
    if (initialParamsLoaded) {
      const params = new URLSearchParams(window.location.search);

      if (dateSelect) {
        params.set("dateSelect", dateSelect.toISOString());
      } else {
        params.delete("dateSelect");
      }

      if (search) {
        params.set("search", search);
      } else {
        params.delete("search");
      }

      if (companySelected?.idCompany) {
        params.set("companySelected", companySelected.idCompany);
      } else {
        params.delete("companySelected");
      }

      if (interimAgencySelected?.length > 0) {
        const agencies = interimAgencySelected
          .map((interim) => interim.name)
          .join(",");
        params.set("interimAgencySelected", agencies);
      } else {
        params.delete("interimAgencySelected");
      }

      if (interimSectorSelected?.length > 0) {
        const sectors = interimSectorSelected
          .map((interim) => interim.name)
          .join(",");
        params.set("interimSectorSelected", sectors);
      } else {
        params.delete("interimSectorSelected");
      }

      const selectedMissions = missionStatusList
        ?.filter((missionStatus) => missionStatus.checked)
        .map((missionStatus) => missionStatus.name);

      if (selectedMissions?.length > 0) {
        params.set("missionStatusList", selectedMissions.join(","));
      } else {
        params.delete("missionStatusList");
      }

      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState(null, "", newUrl);
    }
  }, [
    dateSelect,
    search,
    companySelected,
    interimAgencySelected,
    interimSectorSelected,
    missionStatusList,
    initialParamsLoaded,
  ]);

  const manageGetMission = useCallback(async (silent = false) => {
    if (!initialParamsLoaded) return;

    if (!silent) setIsWaitingMissions(true);
    isLoadingRef.current = true;

    try {
      const idCompany = companySelected?.idCompany || "";

      if (idCompany) {
        const getMissionsPromise = () =>
          new Promise((resolve, reject) => {
            const expectedRange = calculateDateRange(dateSelect, 2, 2);
            loadedDateRangeRef.current = { min: expectedRange.min, max: expectedRange.max };
            
            ApiDatabase.getMissions(
              {
                idCompany,
                interimAgencies:
                  interimAgencySelected.length > 0
                    ? JSON.stringify(
                        interimAgencySelected.map((agency) => agency.name)
                      )
                    : [],
                interimSectors:
                  interimSectorSelected.length > 0
                    ? JSON.stringify(
                        interimSectorSelected.map((sector) => sector.name)
                      )
                    : [],
                search,
                date: dateToYYYYMMDD(dateSelect),
                loadedMin: dateToYYYYMMDD(expectedRange.min),
                loadedMax: dateToYYYYMMDD(expectedRange.max),
              },
              (data) => resolve(data),
              (err) => reject(err)
            );
          });

        const missionData = await getMissionsPromise();

        if (idCompany === missionData.companyId) {
          const newMissions = missionData.missions || [];
          const wasReset = updateLoadedDateRange(dateSelect);
          
          if (wasReset || !loadedDateRangeRef.current.min) {
            setMissions(newMissions);
          } else {
            setMissions((prevMissions) => {
              const existingKeys = new Set(
                prevMissions.map((m) => `${m._id}-${new Date(m.date).toISOString().split("T")[0]}`)
              );
              const uniqueNewMissions = newMissions.filter(
                (m) => !existingKeys.has(`${m._id}-${new Date(m.date).toISOString().split("T")[0]}`)
              );
              return [...prevMissions, ...uniqueNewMissions];
            });
          }
        }
        isLoadingRef.current = false;
        if (!silent) setIsWaitingMissions(false);
      } else {
        setMissions([]);
        isLoadingRef.current = false;
        if (!silent) setIsWaitingMissions(false);
      }
    } catch (err) {
      if (err.name !== "CanceledError") {
        console.log(err);
        isLoadingRef.current = false;
        if (!silent) setIsWaitingMissions(false);
      }
    }
  }, [
    initialParamsLoaded,
    companySelected?.idCompany,
    interimAgencySelected,
    interimSectorSelected,
    search,
    dateSelect,
    setIsWaitingMissions,
    setMissions,
  ]);

  useEffect(() => {
    if (initialParamsLoaded) {
      resetLoadedDateRange();
      setMissions([]);
      manageGetMission();
    }
    // eslint-disable-next-line
  }, [
    initialParamsLoaded,
    companySelected,
    interimAgencySelected,
    interimSectorSelected,
  ]);

  useEffect(() => {
    if (initialParamsLoaded) {
      setIsWaitingMissions(true);
      resetLoadedDateRange();
      setMissions([]);
      timerGetMissionSuggestions.current = setTimeout(async () => {
        await manageGetMission();
      }, 300);

      return () => clearTimeout(timerGetMissionSuggestions.current);
    }
    // eslint-disable-next-line
  }, [initialParamsLoaded, search]);

  useEffect(() => {
    if (initialParamsLoaded && dateSelect) {
      const dateInRange = isDateInLoadedRange(dateSelect);
      const fullRangeLoaded = isFullRangeLoaded(dateSelect);
      const isLoading = isLoadingRef.current;

      if (!dateInRange) {
        setIsWaitingMissions(true);
        timerGetMissionSuggestions.current = setTimeout(async () => {
          await manageGetMission();
        }, 300);
        return () => clearTimeout(timerGetMissionSuggestions.current);
      } else {
        const dateStr = dateSelect.toISOString().split("T")[0];
        const hasMissionsForDate = missions.some(
          (m) => new Date(m.date).toISOString().split("T")[0] === dateStr
        );
        
        if (!fullRangeLoaded && !hasMissionsForDate) {
          setIsWaitingMissions(true);
          timerGetMissionSuggestions.current = setTimeout(async () => {
            await manageGetMission();
          }, 300);
          return () => clearTimeout(timerGetMissionSuggestions.current);
        } else if (!isLoading) {
          setIsWaitingMissions(false);
        }
        
        if (!fullRangeLoaded) {
          timerGetMissionSuggestions.current = setTimeout(async () => {
            await manageGetMission(true);
          }, 300);
          return () => clearTimeout(timerGetMissionSuggestions.current);
        }
      }
    }
    // eslint-disable-next-line
  }, [initialParamsLoaded, dateSelect]);

  useEffect(() => {
    if (slideOverDataReturn.missions) {
      setRefreshMissions(!refreshMissions);
      setSlideOverDataReturn({});
    }
  }, [slideOverDataReturn, setSlideOverDataReturn, refreshMissions]);

  useEffect(() => {
    ApiDatabase.getInterimAgencyMissions(
      {},
      (data) => {
        if (data.interimAgencies?.length > 0) {
          const selectedAgencyNames = interimAgencySelected?.map(
            (agency) => agency.name
          );

          data.interimAgencies.forEach((agency) => {
            agency.checked = selectedAgencyNames.includes(agency.name);
          });

          setInterimAgencyList(data.interimAgencies);
        } else {
          setInterimAgencyList([]);
        }
      },
      (err) => {
        console.log(err);
      }
    );
  }, [
    refreshMissions,
    setInterimAgencySelected,
    setInterimAgencyList,
    interimAgencySelected,
  ]);

  useEffect(() => {
    ApiDatabase.getInterimSectorMissions(
      {},
      (data) => {
        if (data.interimSectors?.length > 0) {
          const selectedSectorNames = interimSectorSelected?.map(
            (agency) => agency.name
          );

          data.interimSectors.forEach((sector) => {
            sector.checked = selectedSectorNames.includes(sector.name);
          });

          setInterimSectorList(data.interimSectors);
        } else {
          setInterimSectorList([]);
        }
      },
      (err) => {
        console.log(err);
      }
    );
  }, [
    refreshMissions,
    setInterimSectorSelected,
    setInterimSectorList,
    interimSectorSelected,
  ]);

  const navigateToMission = (idMission, date) => {
    const params = new URLSearchParams(window.location.search);
    params.set("date", date);
    const newUrl = `/hourly-tracking/${idMission}?${params.toString()}`;
    navigate(newUrl);
  };

  return (
    <PageContainer
      type={"hourly_tracking"}
      dateSelect={dateSelect}
      setDateSelect={setDateSelect}
    >
      <div className={"flex flex-1 flex-col gap-4 h-full"}>
        <NavBarHourlyTracking
          dateSelect={dateSelect}
          setDateSelect={setDateSelect}
          companySelected={companySelected}
          companyList={companyList}
          setCompanySelected={setCompanySelected}
          setCompanyList={setCompanyList}
          interimAgencyList={interimAgencyList}
          setInterimAgencyList={setInterimAgencyList}
          interimAgencySelected={interimAgencySelected}
          setInterimAgencySelected={setInterimAgencySelected}
          interimSectorList={interimSectorList}
          setInterimSectorList={setInterimSectorList}
          interimSectorSelected={interimSectorSelected}
          setInterimSectorSelected={setInterimSectorSelected}
          search={search}
          setSearch={setSearch}
          missionStatusSelected={missionStatusSelected}
          setMissionStatusSelected={setMissionStatusSelected}
          missionStatusList={missionStatusList}
          setMissionStatusList={setMissionStatusList}
        />
        <div className={"h-0.5 w-full bg-gray-100"} />
        <div className={"flex flex-1 flex-col h-full overflow-auto"}>
          {isWaitingMissions ? (
            <div className={"flex flex-col gap-3 min-w-[830px] animate-pulse"}>
              {[1, 2, 3, 4, 5].map((index) => (
                <TableHourTrackingMission key={index} isWaiting={true} />
              ))}
            </div>
          ) : (
            <>
              {missionsToDisplay.length > 0 ? (
                <div className={"flex flex-col gap-3 min-w-[830px]"}>
                  {missionsToDisplay.map((mission, index) => (
                    <TableHourTrackingMission
                      key={index}
                      mission={mission}
                      navigateToMission={navigateToMission}
                    />
                  ))}
                </div>
              ) : (
                <EmptyState type={"hourly_tracking"} />
              )}
            </>
          )}
        </div>
        <FooterHourlyTracking
          syncDate={companySelected?.syncDate || null}
          badgerName={companySelected?.badgerName || null}
          currentPage={currentPage}
          countPerPage={countPerPage}
          countTotal={countTotal}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
        />
      </div>
    </PageContainer>
  );
};

export default HourlyTracking;
